/**
 * use-update-job — the single shared PATCH /v1/jobs/:id mutation.
 *
 * Both JobDetailModal's edit mode and the Job Board's inline notes cell mutate a
 * job through this one hook (Quick 260721) — there is deliberately NOT a second
 * mutation for notes. It invalidates the job-detail + jobs-list caches on success,
 * then runs the caller's optional onSuccess/onError (toast, close edit mode, etc.).
 *
 * The server (routes/jobs/update.ts) applies each field independently, so a
 * notes-only body (`{ dispatcherNotes }`) is valid — the notes cell sends exactly
 * that.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useApiOpts, useApiMutation } from '@lodex-pro/web-shared';
import { JOB_DETAIL_QUERY_KEY } from './use-job-detail';

/**
 * PATCH body — every field optional; the server sets only those present.
 *
 * NOTE: no `siteId`. The pickup site moved to the new location model in Phase
 * 26.2 (jobs.site_id dropped); routes/jobs/update.ts accepts no site field, so a
 * `siteId` here would be silently stripped server-side. The Job Detail modal's
 * Site field is read-only for that reason — don't reintroduce a site field here
 * without matching server support (see routes/jobs/update.ts).
 */
export interface UpdateJobBody {
  readonly assignedDriverId?: string | null;
  readonly scheduledAt?: string | null;
  readonly dispatcherNotes?: string | null;
  readonly isUrgent?: boolean;
  // Legacy "New Request" parity — editable requester contact fields (null clears).
  readonly requesterName?: string | null;
  readonly requesterPhone?: string | null;
  readonly requesterEmail?: string | null;
  // Deferred item 09.1-09 — re-select the job's Job Type. Included only when it
  // actually changed; the server re-validates (capture guardrail + customer
  // scope) and is authoritative (see routes/jobs/update.ts).
  readonly jobTypeId?: number;
}

export interface UseUpdateJobOptions {
  readonly onSuccess?: () => void;
  /** OPEN-157: the error, so a caller can word a JOB_NOT_REASSIGNABLE refusal. */
  readonly onError?: (err: unknown) => void;
}

export function useUpdateJob(
  jobId: string | null,
  opts?: UseUpdateJobOptions,
): ReturnType<typeof useApiMutation<UpdateJobBody, { job: { id: string } }>> {
  const apiOpts = useApiOpts();
  const queryClient = useQueryClient();
  return useApiMutation<UpdateJobBody, { job: { id: string } }>(
    apiOpts,
    `/v1/jobs/${jobId ?? ''}`,
    'PATCH',
    {
      onSuccess: () => {
        if (jobId !== null && jobId !== '') {
          void queryClient.invalidateQueries({ queryKey: JOB_DETAIL_QUERY_KEY(jobId) });
        }
        void queryClient.invalidateQueries({ queryKey: ['jobs'] });
        opts?.onSuccess?.();
      },
      onError: (err: unknown) => {
        opts?.onError?.(err);
      },
    },
  );
}
